import React, { useEffect, useRef, useState } from 'react';
import PageHero from '../components/PageHero';
import Seo from '../components/Seo';
import { Phone, Mail, MapPin, Clock, Send, Navigation, AlertCircle } from 'lucide-react';
import { CONTACT, IMAGES } from '../mock';
import { validateName, validateEmail, validateMessage, isSuspiciousDuplicate } from '../lib/validation';

const RATE_LIMIT_MS = 60_000;

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k]))
    .join('&');

export default function Contact() {
  const [form, setForm] = useState({ first:'', last:'', email:'', phone:'', insurance:'', insuranceName:'', message:'' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const mountedAtRef = useRef(Date.now());
  useEffect(() => { mountedAtRef.current = Date.now(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const touch = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  // Per-field validation errors (null when valid).
  const errors = {
    first: validateName(form.first, 'First name'),
    last: validateName(form.last, 'Last name'),
    email: validateEmail(form.email),
    message: validateMessage(form.message, 5),
  };
  const isFormValid = !errors.first && !errors.last && !errors.email && !errors.message;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Anti-bot #1 — honeypot fields
    const hp1 = e.target.querySelector('input[name="bot-field"]')?.value || '';
    const hp2 = e.target.querySelector('input[name="website"]')?.value || '';
    if (hp1.trim() || hp2.trim()) { setSent(true); return; }

    // Anti-bot #2 — minimum time on page
    if (Date.now() - mountedAtRef.current < 2500) {
      setError('Please take a moment to review your details before submitting.');
      return;
    }

    // Field-level validation
    const firstErr = validateName(form.first, 'First name');
    const lastErr = validateName(form.last, 'Last name');
    const emailErr = validateEmail(form.email);
    const msgErr = validateMessage(form.message, 5);
    if (firstErr || lastErr || emailErr || msgErr) {
      setError(firstErr || lastErr || emailErr || msgErr);
      setTouched({ first: true, last: true, email: true, message: true });
      return;
    }

    // Cross-field: reject if all identical (bot signal)
    if (isSuspiciousDuplicate([form.first, form.last, form.email, form.message])) {
      setError('Please fill in each field with different content.');
      return;
    }

    // Rate limit
    try {
      const lastAt = parseInt(localStorage.getItem('ccc_last_submit') || '0', 10);
      if (lastAt && Date.now() - lastAt < RATE_LIMIT_MS) {
        setError('You just submitted a message — please wait a minute before sending another.');
        return;
      }
    } catch (_) { /* noop */ }

    setSubmitting(true);
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'contact',
          'bot-field': '',
          website: '',
          ...form,
        }),
      });
      try { localStorage.setItem('ccc_last_submit', String(Date.now())); } catch(_) { /* noop */ }
    } catch (_) { /* swallow — fallback below */ }
    try { const list = JSON.parse(localStorage.getItem('ccc_contact') || '[]'); list.push({ ...form, ts: Date.now() }); localStorage.setItem('ccc_contact', JSON.stringify(list)); } catch(_){ /* noop */ }
    setSubmitting(false);
    setSent(true);
    setForm({ first:'', last:'', email:'', phone:'', insurance:'', insuranceName:'', message:'' });
  };
  return (
    <>
      <Seo
        title="Contact Chesapeake Counseling Center"
        description="Get in touch with Chesapeake Counseling Center at 757-302-8362 or admin@chesapeakecounselingcenter.com. In-person at 501 Cedar Rd., Suite 2A, Chesapeake, VA."
        path="/contact"
      />
      <PageHero
        eyebrow="Contact"
        title="Get in"
        highlight="Touch"
        subtitle="We’re here to answer any questions. If you’re a new client, please complete the form or give us a call."
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Contact' }]}
      />

      {/* Thrive banner */}
      <section className="relative overflow-hidden" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 md:py-10 reveal">
          <img src={IMAGES.thrive} alt="You were not meant to just survive. You were meant to thrive." className="w-full h-auto rounded-2xl shadow-[0_30px_60px_-30px_rgba(78,87,69,0.2)]" />
        </div>
      </section>

      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-10">
          <div className="reveal">
            <h2 className="font-serif-display text-3xl mb-4 text-[color:var(--deep-sage)]">Reach out to our team</h2>
            <p className="text-[color:var(--taupe-gray)] mb-8 leading-relaxed">Let us know how we can help — we’ll respond as soon as we can and help you find the right next step.</p>

            <div className="space-y-5 text-[color:var(--deep-sage)]">
              <div className="flex gap-3"><Phone className="w-5 h-5 text-[color:var(--dusty-rose)] mt-1" /><div><div className="font-semibold">Phone</div><a href={CONTACT.phoneHref} className="text-[color:var(--taupe-gray)] hover:text-[color:var(--dusty-rose)]">{CONTACT.phone}</a></div></div>
              <div className="flex gap-3"><Mail className="w-5 h-5 text-[color:var(--dusty-rose)] mt-1" /><div><div className="font-semibold">Email</div><a href={`mailto:${CONTACT.email}`} className="text-[color:var(--taupe-gray)] hover:text-[color:var(--dusty-rose)] break-all">{CONTACT.email}</a></div></div>
              <div className="flex gap-3"><MapPin className="w-5 h-5 text-[color:var(--dusty-rose)] mt-1" /><div><div className="font-semibold">Office</div><div className="text-[color:var(--taupe-gray)]">{CONTACT.address}<br />{CONTACT.city}</div></div></div>
              <div className="flex gap-3"><Clock className="w-5 h-5 text-[color:var(--dusty-rose)] mt-1" /><div><div className="font-semibold">Hours</div><div className="text-[color:var(--taupe-gray)] text-sm space-y-0.5">{CONTACT.hours.map(h => <div key={h.day}>{h.day}: {h.time}</div>)}</div></div></div>
            </div>
          </div>

          <div className="card-soft p-8 reveal">
            {sent ? (
              <div className="text-center py-10">
                <div className="font-script text-3xl text-[color:var(--dusty-rose)] mb-2">Thank you</div>
                <p className="text-[color:var(--taupe-gray)]">Your message has been received. We’ll be in touch shortly.</p>
                <button onClick={() => setSent(false)} className="mt-6 btn-secondary">Send another</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} name="contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" className="space-y-4">
                <input type="hidden" name="form-name" defaultValue="contact" />
                <p hidden><label>Don&apos;t fill this out: <input name="bot-field" defaultValue="" /></label></p>
                <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                  <label>Website<input type="text" name="website" tabIndex="-1" autoComplete="off" defaultValue="" /></label>
                </div>
                {error && (
                  <div className="rounded-xl px-4 py-3 text-sm flex items-start gap-2" style={{ background: 'rgba(232,165,169,0.15)', color: '#8B3A44' }}>
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="First Name" name="first" value={form.first} onChange={set('first')} onBlur={touch('first')} required error={touched.first && errors.first} />
                  <Field label="Last Name" name="last" value={form.last} onChange={set('last')} onBlur={touch('last')} required error={touched.last && errors.last} />
                </div>
                <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set('phone')} />
                <Field label="Email" name="email" type="email" value={form.email} onChange={set('email')} onBlur={touch('email')} required error={touched.email && errors.email} />
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold" htmlFor="contact-insurance">
                    Insurance / Payment
                  </label>
                  <select
                    id="contact-insurance"
                    name="insurance"
                    value={form.insurance}
                    onChange={set('insurance')}
                    className="mt-1.5 w-full rounded-xl border-2 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)] transition"
                    style={{ borderColor: 'var(--soft-border)' }}
                  >
                    <option value="">Select insurance or payment method…</option>
                    <option value="Aetna">Aetna</option>
                    <option value="Anthem / BCBS">Anthem / Blue Cross Blue Shield</option>
                    <option value="Cigna">Cigna</option>
                    <option value="Optum / United Healthcare">Optum / United Healthcare</option>
                    <option value="Tricare">Tricare</option>
                    <option value="Medicaid">Medicaid</option>
                    <option value="Medicare">Medicare</option>
                    <option value="Other Insurance">Other insurance</option>
                    <option value="Self-Pay / Cash">Self-pay / Cash</option>
                    <option value="Not Sure">Not sure yet</option>
                  </select>
                  <p className="mt-1 text-[11px] text-[color:var(--taupe-gray)]">Let us know what insurance you plan to use, or if you’ll be paying cash / self-pay.</p>
                </div>
                <Field label="Insurance / Plan Name" name="insuranceName" value={form.insuranceName} onChange={set('insuranceName')} placeholder="e.g., Anthem HealthKeepers, Tricare Prime, Aetna PPO…" />
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold flex items-center justify-between" htmlFor="contact-message">
                    <span>Message</span>
                    {touched.message && errors.message && <span className="text-[10px] normal-case tracking-normal text-[color:var(--dusty-rose)] font-medium">{errors.message}</span>}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={set('message')}
                    onBlur={touch('message')}
                    required
                    className="mt-1.5 w-full rounded-xl border-2 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)] transition"
                    style={{ borderColor: touched.message && errors.message ? '#E8A5A9' : 'var(--soft-border)' }}
                  />
                </div>
                <button type="submit" disabled={submitting || !isFormValid} className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed" title={!isFormValid ? 'Please complete First name, Last name, Email, and Message.' : ''}><Send className="w-4 h-4" /> {submitting ? 'Sending…' : 'Submit'}</button>
                {!isFormValid && (
                  <div className="text-xs text-center text-[color:var(--taupe-gray)]">
                    Please fill in your name, a valid email, and a message to submit.
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Service Area / Site Map */}
      <section className="section-pad" style={{ background: 'var(--cream)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Where We Serve</div>
            <h2 className="section-title mb-3">Our <span className="text-[color:var(--dusty-rose)]">Service Area</span></h2>
            <p className="text-[color:var(--taupe-gray)] max-w-2xl mx-auto leading-relaxed">
              Chesapeake Counseling Center is located in Chesapeake, Virginia and serves clients in Chesapeake, Virginia Beach, Norfolk, Suffolk, and surrounding Hampton Roads areas. Telehealth sessions are also available throughout Virginia.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-stretch">
            {/* Map embed */}
            <div className="card-soft overflow-hidden reveal">
              <iframe
                title="Chesapeake Counseling Center — Service Area Map"
                src="https://www.google.com/maps?q=501+Cedar+Rd+Suite+2A+Chesapeake+VA+23322&z=10&output=embed"
                width="100%"
                height="420"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <div className="p-5 flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[color:var(--dusty-rose)] mt-0.5" />
                <div>
                  <div className="font-semibold text-[color:var(--deep-sage)]">Our Office</div>
                  <div className="text-sm text-[color:var(--taupe-gray)]">{CONTACT.address}<br />{CONTACT.city}</div>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=501+Cedar+Rd+Suite+2A+Chesapeake+VA+23322"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-sm text-[color:var(--dusty-rose)] hover:underline font-semibold"
                  >
                    <Navigation className="w-4 h-4" /> Get directions
                  </a>
                </div>
              </div>
            </div>

            {/* Cities served */}
            <div className="card-soft p-8 reveal" style={{ background: 'linear-gradient(135deg, rgba(232,165,169,0.06), rgba(184,190,164,0.10))' }}>
              <div className="eyebrow mb-2">Communities We Serve</div>
              <h3 className="font-serif-display text-2xl text-[color:var(--deep-sage)] mb-4">In-Person & Nearby</h3>
              <ul className="grid grid-cols-2 gap-y-2 gap-x-4 text-[color:var(--deep-sage)] mb-6">
                {['Chesapeake, VA', 'Virginia Beach, VA', 'Norfolk, VA', 'Suffolk, VA', 'Portsmouth, VA', 'Hampton, VA', 'Newport News, VA', 'Currituck, NC'].map((city) => (
                  <li key={city} className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-[color:var(--dusty-rose)] flex-shrink-0" />
                    <span>{city}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t" style={{ borderColor: 'var(--soft-border)' }}>
                <div className="eyebrow mb-2">Statewide Telehealth</div>
                <p className="text-sm text-[color:var(--taupe-gray)] leading-relaxed">
                  Secure, HIPAA-compliant video sessions are available to clients throughout Virginia — the same warm, personalized care from wherever you are.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, name, error, ...rest }) {
  const id = `contact-${name || label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div>
      <label htmlFor={id} className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold flex items-center justify-between">
        <span>{label}</span>
        {error && <span className="text-[10px] normal-case tracking-normal text-[color:var(--dusty-rose)] font-medium">{error}</span>}
      </label>
      <input
        id={id}
        name={name}
        {...rest}
        className="mt-1.5 w-full rounded-xl border-2 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)] transition"
        style={{ borderColor: error ? '#E8A5A9' : 'var(--soft-border)' }}
      />
    </div>
  );
}
